import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Eye, BarChart3, TrendingUp, Users, Clock, ExternalLink } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ViewAnalyticsPage() {
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [viewerDetails, setViewerDetails] = useState(null);

  useEffect(() => {
    fetchViews();
  }, []);

  const fetchViews = async () => {
    try {
      const res = await axios.get(`${API}/admin/all-views`, { headers: getAuthHeaders() });
      setDeals(res.data);
    } catch (error) {
      console.error('Failed to fetch view analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const openViewDetails = async (deal) => {
    setSelectedDeal(deal);
    try {
      const res = await axios.get(`${API}/admin/deal-views/${deal.id}`, { headers: getAuthHeaders() });
      setViewerDetails(res.data);
    } catch (error) {
      console.error('Failed to fetch deal views:', error);
    }
  };

  if (user?.role !== 'admin' && user?.role !== 'internal') {
    return <div className="p-6 text-center text-muted-foreground">Admin access required</div>;
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-muted-foreground">Loading analytics...</div></div>;
  }

  const totalViews = deals.reduce((sum, d) => sum + (d.views || 0), 0);
  const totalViewers = new Set(deals.flatMap(d => (d.viewed_by || []).map(v => v.user_id))).size;

  return (
    <div className="p-6 space-y-6" data-testid="view-analytics-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Listing View Analytics</h1>
        <p className="text-muted-foreground">Track who viewed each deal listing</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Viewed Listings</p>
                <p className="text-2xl font-bold font-mono">{deals.length}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Views</p>
                <p className="text-2xl font-bold font-mono">{totalViews}</p>
              </div>
              <Eye className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Unique Viewers</p>
                <p className="text-2xl font-bold font-mono">{totalViewers}</p>
              </div>
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Views/Deal</p>
                <p className="text-2xl font-bold font-mono">{deals.length > 0 ? (totalViews / deals.length).toFixed(1) : '0'}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Deal View Rankings</CardTitle>
          <CardDescription>Deals sorted by total views</CardDescription>
        </CardHeader>
        <CardContent>
          {deals.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No view data available yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Deal</TableHead>
                  <TableHead>Sector</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead className="text-right">Views</TableHead>
                  <TableHead className="text-right">Unique Viewers</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deals.map((deal, idx) => (
                  <TableRow key={deal.id} data-testid={`view-row-${deal.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-muted-foreground w-5">{idx + 1}.</span>
                        <span className="font-medium text-sm cursor-pointer hover:underline" onClick={() => navigate(`/deals/${deal.id}`)}>
                          {deal.name}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{deal.sector}</Badge></TableCell>
                    <TableCell className="text-sm">{deal.country}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{deal.views}</TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">{(deal.viewed_by || []).length}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => openViewDetails(deal)} data-testid={`view-details-${deal.id}`}>
                        <Eye size={14} className="mr-1" />Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selectedDeal} onOpenChange={() => { setSelectedDeal(null); setViewerDetails(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye size={18} />View Details: {selectedDeal?.name}</DialogTitle>
          </DialogHeader>
          {viewerDetails ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4 text-sm">
                <span className="text-muted-foreground">Total Views:</span>
                <span className="font-mono font-bold">{viewerDetails.total_views}</span>
              </div>
              {viewerDetails.viewers && viewerDetails.viewers.length > 0 ? (
                <div className="max-h-64 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Viewed At</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {viewerDetails.viewers.map((v, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-sm font-medium">{v.name || v.user_id}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Clock size={12} />
                              {v.viewed_at ? new Date(v.viewed_at).toLocaleString() : 'N/A'}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No detailed viewer data available.</p>
              )}
            </div>
          ) : (
            <div className="text-center py-4 text-muted-foreground">Loading viewer details...</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
